import { useState } from 'react';
import { UM } from '../_components';
import type { Building } from './types';

// ─── Types ───────────────────────────────────────────────────────────────────

type CharRow = { label: string; value: string | number | undefined | null; quality?: 'calculated' | 'manual' | 'imported'; source?: string };
type CharSection = { title: string; rows: CharRow[] };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const qualityColor = (q?: string) => q === 'imported' ? '#059669' : q === 'manual' ? '#d97706' : '#6366f1';
const qualityLabel = (q?: string) => q === 'imported' ? 'Importé' : q === 'manual' ? 'Manuel' : 'Calculé';

const heatingLabel = (type: string) => type === 'Gas' ? 'Gaz' : type === 'Electric' ? 'Électricité' : 'Réseau de chaleur';

// ─── Main component ──────────────────────────────────────────────────────────

interface CharacteristicsTabProps {
  building: Building;
}

export function CharacteristicsTab({ building }: CharacteristicsTabProps) {
  const [selectedTab, setSelectedTab] = useState<'general' | 'hot' | 'cold'>('general');

  const generalSections: CharSection[] = [
    { title: 'Identification', rows: [
      { label: 'Adresse', value: `${building.address}, ${building.postalCode} ${building.city}`, quality: 'imported', source: 'BAN / RNB' },
      { label: 'Usage principal', value: building.purpose, quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Usage secondaire', value: building.secondaryPurpose, quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Nombre de niveaux', value: building.storeys, quality: 'imported', source: 'BDNB' },
    ] },
    { title: 'Surface', rows: [
      { label: 'Surface utile (m²)', value: building.totalArea, quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Surface au sol (m²)', value: Math.round(building.totalArea / (building.storeys || 1)), quality: 'imported', source: 'BDNB' },
    ] },
    { title: 'Performance énergétique', rows: [
      { label: 'DPE', value: building.dpe, quality: 'imported', source: 'ADEME' },
      { label: 'Année de construction', value: building.constructionYear, quality: 'imported', source: 'BDNB' },
      { label: 'Année de rénovation', value: building.renovationYear, quality: 'manual', source: 'Utilisateur' },
      { label: 'Période de construction', value: building.constructionYear < 1975 ? 'Avant 1975' : building.constructionYear < 2000 ? '1975-2000' : 'Après 2000', quality: 'calculated', source: 'Modèle UrbanMap' },
    ] },
    { title: 'Propriétaire', rows: [
      { label: 'Type', value: building.publicPrivate === 'Public' ? 'Public' : 'Privé', quality: 'imported', source: 'DGFiP' },
    ] },
  ];

  const hotSections: CharSection[] = [
    { title: 'Chauffage', rows: [
      { label: 'Mode de chauffage', value: 'Collectif', quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Énergie principale', value: heatingLabel(building.heatingType), quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Type de générateur', value: 'Chaudière condensation', quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Année installation', value: building.constructionYear + 10, quality: 'calculated', source: 'Modèle UrbanMap' },
    ] },
    { title: 'Eau Chaude Sanitaire', rows: [
      { label: 'Mode de production', value: 'Collectif', quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Énergie', value: heatingLabel(building.hotWaterType), quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Part ECS (%)', value: '30', quality: 'calculated', source: 'Modèle UrbanMap' },
    ] },
    { title: 'Installation', rows: [
      { label: 'Type émetteur', value: 'Radiateurs', quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Régulation', value: 'Programmation + robinets thermostatiques', quality: 'calculated', source: 'Modèle UrbanMap' },
    ] },
    { title: 'Réseau', rows: [
      { label: 'Raccordé réseau de chaleur', value: building.heatingType === 'DistrictHeating' ? 'Oui' : 'Non', quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Distance réseau (m)', value: 327, quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Réseau le plus proche', value: 'RC Brétigny-sur-Orge', quality: 'imported', source: 'France Chaleur Urbaine' },
    ] },
  ];

  const coldSections: CharSection[] = [
    { title: 'Climatisation', rows: [
      { label: 'Mode de climatisation', value: 'Individuel', quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Énergie principale', value: 'Électricité', quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Type de générateur', value: 'Split', quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Surface climatisée (%)', value: '30', quality: 'calculated', source: 'Modèle UrbanMap' },
    ] },
    { title: 'Installation', rows: [
      { label: 'Type émetteur', value: 'Splits muraux', quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Puissance installée (kW)', value: Math.round(building.totalArea * 0.05), quality: 'calculated', source: 'Modèle UrbanMap' },
    ] },
    { title: 'Réseau', rows: [
      { label: 'Raccordé réseau de froid', value: 'Non', quality: 'calculated', source: 'Modèle UrbanMap' },
      { label: 'Distance réseau de froid (m)', value: 842, quality: 'calculated', source: 'Modèle UrbanMap' },
    ] },
  ];

  const currentSections = selectedTab === 'general' ? generalSections : selectedTab === 'hot' ? hotSections : coldSections;

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
      {/* Segmented control */}
      <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: `1px solid ${UM.border}`, marginBottom: 16 }}>
        {([['general', 'Général'], ['hot', 'Énergies chaud'], ['cold', 'Énergies froid']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setSelectedTab(key)}
            style={{
              flex: 1, padding: '8px 12px', border: 'none', fontSize: 13,
              fontWeight: selectedTab === key ? 600 : 400, cursor: 'pointer',
              background: selectedTab === key ? '#1e40af' : UM.bgHeader,
              color: selectedTab === key ? '#fff' : UM.text,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {currentSections.map((section) => (
          <div key={section.title}>
            <div style={{ padding: '8px 12px', background: '#f3f4f6', fontWeight: 700, fontSize: 13, color: UM.text, borderTop: '1px solid #e5e7eb' }}>
              {section.title}
            </div>
            {section.rows.filter((r) => r.value !== undefined && r.value !== null).map((row) => (
              <div key={row.label} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f1f5f9', padding: '6px 12px', fontSize: 12, gap: 0 }}>
                <div style={{ width: 230, flexShrink: 0, color: '#374151' }}>{row.label}</div>
                <div style={{ width: 280, flexShrink: 0, fontWeight: 700, background: '#dbeafe', padding: '4px 8px', borderRadius: 2 }}>{row.value}</div>
                <div style={{ width: 80, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 32, height: 16, borderRadius: 8, background: qualityColor(row.quality), position: 'relative', cursor: 'pointer' }} title={qualityLabel(row.quality)}>
                    <div style={{ width: 12, height: 12, borderRadius: 6, background: '#fff', position: 'absolute', top: 2, right: 2 }} />
                  </div>
                </div>
                <div style={{ flex: 1, color: UM.textMuted, fontSize: 11 }}>{row.source}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
